import "./WebchatBotChatBubble.less";

import { MessageApiShared } from "@Api/MessageApiShared";
import MessageComponent from "@Components/shared/Message/Message";
import { BotTextMessage } from "@Src/hubs/chat/dtos/BotTextMessage";
import { ClientRole } from "@Src/hubs/chat/dtos/ClientRole";
import { PersistedMessage } from "@Src/hubs/chat/dtos/PersistedMessage";
import { PersistedMessageType } from "@Src/hubs/chat/dtos/PersistedMessageType";
import {
    getBotAnswerFeedbackData,
    getMessageId,
    getShouldOfferMoreAssistance,
    isHeroCard,
} from "@Utilities/ActivityHelper";
import { Activity, HeroCard, Message } from "botframework-directlinejs";
import BotAnswerFeedback from "./BotAnswerFeedback";
import { hooks } from "botframework-webchat";
import { useAppSelector } from "@Components/CustomerApp/hooks";
import { useState } from "react";
import { ActivityName } from "@Models/chatBot/ActivityName";
import { MessageBackValueProperties } from "@Models/chatBot/MessageBackValueProperties";
import { RecordChatEventRequest } from "@Src/hubs/chat/dtos/RecordChatEventRequest";

interface WebchatBotChatBubbleProps {
    activity: Activity;
    chatId: string;
    isFirstConsecutive: boolean;
    isLastConsecutive: boolean;
    isLastMessage: boolean;
}

const WebchatBotChatBubble = ({
    activity,
    chatId,
    isFirstConsecutive,
    isLastConsecutive,
    isLastMessage,
}: WebchatBotChatBubbleProps): JSX.Element => {
    const webchatSendMessage = hooks.useSendMessage();
    const webchatSendEvent = hooks.useSendEvent();
    const webchatSendMessageBack = hooks.useSendMessageBack();
    const chatApi = useAppSelector((state) => state.customerApp.chatApi);
    const areOperatorsOnline = useAppSelector(
        (state) => state.customerApp.operatorsOnline
    );
    const [showChatWithSpecialistButton, setShowChatWithSpecialistButton] =
        useState<boolean>(false);

    const messageActivity = activity as Message;
    const heroCard = messageActivity.attachments?.[0] as HeroCard;
    const messageText = isHeroCard(activity)
        ? heroCard.content.text
        : messageActivity.text;
    const messageId = getMessageId(activity) as string;
    const noopSendMessageAsUser: (
        message: PersistedMessage
    ) => Promise<string> = async () => {
        return "";
    };

    const message = new BotTextMessage({
        timestamp: activity.timestamp as string,
        chatId: chatId,
        isBotChoice: false,
        text: messageText ?? "",
        textOrLinkArray: [],
        id: activity.id as string,
        senderName: "Pepper",
        senderUserId: "",
        clientRole: ClientRole.Operator,
        messageType: PersistedMessageType.BotText,
    });

    const botAnswerFeedbackData = getBotAnswerFeedbackData(activity);

    const onFeedbackChange = (
        isHelpful: boolean,
        isNotHelpful: boolean
    ): void => {
        if (isNotHelpful && areOperatorsOnline) {
            setShowChatWithSpecialistButton(true);
        } else {
            setShowChatWithSpecialistButton(false);
        }

        // If it is a product bot answer and like or dislike is set, send feedback to ChatBot
        if (
            botAnswerFeedbackData.productBotChannelData &&
            (isHelpful || isNotHelpful)
        ) {
            webchatSendEvent(ActivityName.SendProductBotAnswerFeedback, {
                IsHelpful: isHelpful,
                ProductBotInteractionKey:
                    botAnswerFeedbackData.productBotChannelData.InteractionKey,
            });
        }
    };

    const suggestedActionButtons = [...(heroCard?.content?.buttons ?? [])];
    const chatWithSpecialistButtonText = "Chat with Specialist";
    const alreadyHasChatWithSpecialist = suggestedActionButtons.some(
        (button) => button.title === chatWithSpecialistButtonText
    );
    const shouldShowChatWithSpecialistButton =
        showChatWithSpecialistButton && !alreadyHasChatWithSpecialist;
    const shouldShowSuggestedActionButtons =
        (suggestedActionButtons.length > 0 ||
            shouldShowChatWithSpecialistButton) &&
        isLastMessage;

    const chatWithSpecialistButtonOnClick = (): void => {
        const recordRequest: RecordChatEventRequest = {
            messageType:
                PersistedMessageType.UserSelectedNotHelpfulAnswerChatWithSpecialist,
        };
        chatApi.recordChatEvent(recordRequest).then(() => {
            webchatSendMessageBack(
                {
                    [MessageBackValueProperties.Keys.SkipConfirmation]:
                        MessageBackValueProperties.Values.SkipConfirmationTrue,
                },
                chatWithSpecialistButtonText,
                chatWithSpecialistButtonText
            );
        });
    };

    const shouldOfferMoreAssistance =
        getShouldOfferMoreAssistance(activity) && isLastMessage;

    const getMessageFooter = (): React.ReactNode => {
        if (!botAnswerFeedbackData.productBotChannelData) {
            return null;
        }

        // AI Disclaimer for product bot answers
        const { productBotChannelData } = botAnswerFeedbackData;
        return (
            <div className="webchat-bot-chat-bubble__ai-disclaimer">
                AI-generated answer for{" "}
                <a
                    href={productBotChannelData.ProductPageLink}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="ai-disclaimer-product-link"
                >
                    Item #{" "}
                    <span
                        className="webchat-bot-chat-bubble__ai-disclaimer__item-number"
                        data-testid="ai-disclaimer-item-number"
                    >
                        {productBotChannelData.ItemNumber}
                    </span>
                </a>
            </div>
        );
    };

    return (
        <div
            className="webchat-bot-chat-bubble"
            data-testid="webchat-bot-chat-activity"
        >
            <MessageComponent
                key={messageId}
                message={message}
                isMineOrOperatorSeeingBot={false}
                isFirstConsecutive={isFirstConsecutive}
                isLastConsecutive={isLastConsecutive}
                sendMessageAsUser={noopSendMessageAsUser}
                isChatHubConnected={false}
                smartIncludesEnabled={false}
                messageApi={{} as MessageApiShared}
                avatar={""}
                baseImageRoute={""}
                onRerenderNeeded={(): void => {
                    return;
                }}
                useRelativeTime={true}
                messageFooter={getMessageFooter()}
            />
            {botAnswerFeedbackData.shouldShowFeedbackButtons && (
                <BotAnswerFeedback
                    botAnswerFeedbackData={botAnswerFeedbackData}
                    onFeedbackChange={onFeedbackChange}
                />
            )}
            {shouldShowSuggestedActionButtons && (
                <div className="suggested-action-buttons">
                    {shouldShowChatWithSpecialistButton && (
                        <button
                            type="button"
                            className="suggested-action-button"
                            onClick={chatWithSpecialistButtonOnClick}
                            data-testid="chat-with-specialist-action-button"
                        >
                            {chatWithSpecialistButtonText}
                        </button>
                    )}
                    {suggestedActionButtons.map((button, index) => {
                        const uniqueKey = `${button.title}-${index}`;
                        return (
                            <button
                                key={uniqueKey}
                                type="button"
                                className="suggested-action-button"
                                onClick={(): void => {
                                    webchatSendMessage(button.title as string);
                                }}
                                data-testid="suggested-action-button"
                            >
                                {button.title}
                            </button>
                        );
                    })}
                </div>
            )}
            {shouldOfferMoreAssistance && (
                <div className="need-more-assistance">
                    <div
                        className="need-more-assistance__text"
                        data-testid="need-more-assistance-action-text"
                    >
                        Need more assistance?
                    </div>
                    <button
                        className="suggested-action-button"
                        onClick={(): void => {
                            webchatSendMessage(chatWithSpecialistButtonText);
                        }}
                        data-testid="need-more-assistance-action-button"
                    >
                        {chatWithSpecialistButtonText}
                    </button>
                </div>
            )}
        </div>
    );
};

export default WebchatBotChatBubble;
